/**
 * Data Store — data sources and field links
 */
import { createStore } from 'zustand/vanilla';
import type { DataSource, DataLink } from '@graphicsim/shared-types';
import { eventBus } from '../engine/eventBus';

let nextSourceNum = 1;
let nextLinkNum = 1;

export interface DataStoreState {
  sources: DataSource[];
  links: DataLink[];
  // Actions
  addSource: (source: Omit<DataSource, 'id'>) => DataSource;
  removeSource: (sourceId: string) => void;
  updateSourceRow: (sourceId: string, rowIndex: number) => void;
  addLink: (link: Omit<DataLink, 'id'>) => DataLink;
  removeLink: (linkId: string) => void;
  getLinksForItem: (projectItemId: string) => DataLink[];
  getLinkedValue: (link: DataLink) => string | undefined;
}

export const dataStore = createStore<DataStoreState>((set, get) => ({
  sources: [],
  links: [],

  addSource: (partial) => {
    const source: DataSource = { ...partial, id: `ds-${Date.now()}-${nextSourceNum++}` };
    set({ sources: [...get().sources, source] });
    eventBus.emit('data:sourceCreated', { source });
    return source;
  },

  removeSource: (sourceId) => {
    set({
      sources: get().sources.filter((s) => s.id !== sourceId),
      links: get().links.filter((l) => l.dataSourceId !== sourceId),
    });
  },

  updateSourceRow: (sourceId, rowIndex) => {
    set({
      sources: get().sources.map((s) =>
        s.id === sourceId ? { ...s, activeRowIndex: rowIndex } : s
      ),
    });
    eventBus.emit('data:rowChanged', { sourceId, rowIndex });
  },

  addLink: (partial) => {
    const link: DataLink = { ...partial, id: `dl-${Date.now()}-${nextLinkNum++}` };
    set({ links: [...get().links, link] });
    eventBus.emit('data:linkCreated', { link });
    return link;
  },

  removeLink: (linkId) => {
    set({ links: get().links.filter((l) => l.id !== linkId) });
  },

  getLinksForItem: (projectItemId) => {
    return get().links.filter((l) => l.projectItemId === projectItemId);
  },

  getLinkedValue: (link) => {
    const source = get().sources.find((s) => s.id === link.dataSourceId);
    if (!source || source.activeRowIndex >= source.rows.length) return undefined;
    return source.rows[source.activeRowIndex][link.columnName];
  },
}));
